import { buildCalendarDateMeta, buildDateMeta } from '../../../utils/dateUtils';

const normalizeKey = (value) => String(value ?? '').trim().replace(/[\s-]+/g, '_').toUpperCase();

const pickFirstValue = (...values) =>
  values.find((value) => value !== null && value !== undefined && String(value).trim() !== '');

const ENROLLED_STATUSES = new Set(['ENROLLED', 'ACTIVE', 'IN_PROGRESS', 'ACTION_REQUIRED']);

const resolveEnrollmentDate = (row) => {
  const candidates = [
    row?.enrollmentDate,
    row?.enrolledDt,
    row?.enrollmentDt,
    row?.enrollmentDateResolved,
    row?._original?.enrollmentDate,
    row?.sourceEnrollment?.enrollmentDate,
  ];
  return candidates.find((value) => value !== null && value !== undefined && value !== '');
};

const resolveCancelDate = (row) => {
  const candidates = [
    row?.cancelDate,
    row?.cancelDt,
    row?.cancelledAt,
    row?.sourceEnrollment?.cancelDate,
    row?.sourceEnrollment?.cancelDt,
  ];
  return candidates.find((value) => value !== null && value !== undefined && value !== '');
};

const resolveCoolingDate = (row) => {
  const candidates = [
    row?.coolingDate,
    row?.coolingAt,
    row?.missingSince,
    row?.sourceEnrollment?.coolingDate,
    row?.sourceEnrollment?.missingSince,
  ];
  return candidates.find((value) => value !== null && value !== undefined && value !== '');
};

const resolveLastUpdateDate = (row) => {
  const candidates = [
    row?.lastUpdateDate,
    row?.lastUpdateDt,
    row?._original?.lastUpdateDate,
    row?.sourceEnrollment?.lastUpdateDate,
  ];
  return candidates.find((value) => value !== null && value !== undefined && value !== '');
};

export const isEnrolledStatus = (status) => ENROLLED_STATUSES.has(normalizeKey(status));

export const flattenEnrollmentsForBac = (allEnrollmentData, bac) => {
  const bacText = String(bac ?? '').trim();
  if (!bacText) return [];
  if (!Array.isArray(allEnrollmentData)) return [];

  return allEnrollmentData.flatMap((programGroup) => {
    const programName = programGroup?.programName || '';
    const rows = Array.isArray(programGroup?.data) ? programGroup.data : [];
    return rows
      .filter((row) => String(row?.bac ?? '').trim() === bacText)
      .map((row) => ({
        ...row,
        programName: row?.programName || programName,
      }));
  });
};

export const buildProgramCardModel = (enrollmentRow) => {
  const status = normalizeKey(enrollmentRow?.enrollmentStatusCd || enrollmentRow?.programStatusCd);
  const isEnrolled = isEnrolledStatus(status);
  const isCancelled = status === 'CANCELLED';
  const isCooling = status === 'COOLING';

  let dateLabel = isEnrolled ? 'Enrollment Date' : 'Status Date';
  let dateValue = isEnrolled ? resolveEnrollmentDate(enrollmentRow) : resolveLastUpdateDate(enrollmentRow);

  if (!isEnrolled && isCancelled) {
    dateLabel = 'Cancel Date';
    dateValue = resolveCancelDate(enrollmentRow) || resolveLastUpdateDate(enrollmentRow);
  }
  if (!isEnrolled && isCooling) {
    dateLabel = 'Cooling Date';
    dateValue = resolveCoolingDate(enrollmentRow) || resolveLastUpdateDate(enrollmentRow);
  }

  return {
    programName: enrollmentRow?.programName || 'Unknown Program',
    status: enrollmentRow?.enrollmentStatusCd || enrollmentRow?.programStatusCd || 'UNKNOWN',
    isEnrolled,
    hoverDateLabel: dateLabel,
    hoverDateDisplay: buildDateMeta(dateValue).display,
    totalSelections: Array.isArray(enrollmentRow?.productSelections) ? enrollmentRow.productSelections.length : 0,
  };
};

export const buildCombinedSelections = (enrollments) => {
  return (Array.isArray(enrollments) ? enrollments : []).flatMap((enrollment, enrollmentIndex) => {
    const programName = enrollment?.programName || 'Unknown Program';
    const programCode = enrollment?.programCode || '';
    const selections = Array.isArray(enrollment?.productSelections) ? enrollment.productSelections : [];

    return selections.map((selection, selectionIndex) => {
      const goLiveMeta = buildCalendarDateMeta(selection?.goLiveDate);
      const cancelMeta = buildCalendarDateMeta(selection?.cancelDate);
      const selectionMeta = buildCalendarDateMeta(
        pickFirstValue(selection?.productSelectionDate, selection?.selectionDate)
      );
      return {
        id: `${programName}-${selection?.referenceId || selection?.serviceItemName || selectionIndex}-${enrollmentIndex}`,
        programName,
        programCode,
        vendorName: selection?.vendorName || selection?.venderCd || '',
        serviceItemName: selection?.serviceItemName || selection?.productDescription || '',
        status: selection?.productSelectionStatus || selection?.status || '',
        selectionDateDisplay: selectionMeta.display,
        selectionDateSort: selectionMeta.sort,
        goLiveDateDisplay: goLiveMeta.display,
        goLiveDateSort: goLiveMeta.sort,
        cancelDateDisplay: cancelMeta.display,
        cancelDateSort: cancelMeta.sort,
        referenceId: selection?.referenceId || '',
      };
    });
  });
};

export const buildCombinedSelectionRowsForTable = (enrollments) => {
  const toDisplayDate = (value) => buildDateMeta(value).display || '';
  const toCalendarDisplayDate = (value) => buildCalendarDateMeta(value).display || '';
  const toBrandsDisplay = (selection, enrollment) =>
    pickFirstValue(
      selection?.brandsDisplay,
      selection?.brandsCsv,
      enrollment?.brandsDisplay,
      enrollment?.brandsCsv,
      Array.isArray(selection?.brandNames) ? selection.brandNames.join(', ') : '',
      Array.isArray(enrollment?.brandNames) ? enrollment.brandNames.join(', ') : ''
    ) || '';

  return (Array.isArray(enrollments) ? enrollments : []).flatMap((enrollment, enrollmentIndex) => {
    const programName = enrollment?.programName || 'Unknown Program';
    const programCode = enrollment?.programCode || '';
    const selections = Array.isArray(enrollment?.productSelections) ? enrollment.productSelections : [];

    return selections.map((selection, selectionIndex) => {
      const id = `${programName}-${selection?.referenceId || selection?.serviceItemName || selectionIndex}-${enrollmentIndex}`;
      const enrollmentDate = pickFirstValue(selection?.enrollmentDate, selection?.enrolledDt, enrollment?.enrollmentDate, enrollment?.enrolledDt);
      const terminationDate = pickFirstValue(
        selection?.terminationDate,
        selection?.isTerminatedDate,
        enrollment?.terminationDate,
        enrollment?.isTerminatedDate
      );
      const optOutDate = pickFirstValue(selection?.optOutDate, selection?.optOutDt, enrollment?.optOutDate, enrollment?.optOutDt);
      const productSelectionDate = pickFirstValue(selection?.productSelectionDate, selection?.selectionDate);
      const vendorProvidedDate = pickFirstValue(selection?.vendorProvidedDate);
      const goLiveDate = pickFirstValue(selection?.goLiveDate);
      const cancelDate = pickFirstValue(selection?.cancelDate);
      const billingStartDate = pickFirstValue(selection?.billingStartDate);
      const billingEndDate = pickFirstValue(selection?.billingEndDate);
      const effectiveDate = pickFirstValue(
        selection?.effectiveDate,
        selection?.effectiveDt,
        selection?.effectiveAt,
        selection?.effectiveOn
      );

      return {
        id,
        ...selection,
        bac: pickFirstValue(selection?.bac, enrollment?.bac, ''),
        dealerName: pickFirstValue(selection?.dealerName, enrollment?.dealerName, ''),
        programName: selection?.programName || programName,
        programCode: selection?.programCode || programCode,
        enrollmentStatusCd: pickFirstValue(selection?.enrollmentStatusCd, enrollment?.enrollmentStatusCd, enrollment?.programStatusCd, ''),
        isTerminated: pickFirstValue(selection?.isTerminated, enrollment?.isTerminated, ''),
        brandsDisplay: toBrandsDisplay(selection, enrollment),
        enrollmentDateDisplay: selection?.enrollmentDateDisplay || toDisplayDate(enrollmentDate),
        terminationDateDisplay: selection?.terminationDateDisplay || toDisplayDate(terminationDate),
        optOutDateDisplay: selection?.optOutDateDisplay || toDisplayDate(optOutDate),
        productSelectionDateDisplay: productSelectionDate
          ? toCalendarDisplayDate(productSelectionDate)
          : selection?.productSelectionDateDisplay || '',
        vendorProvidedDateDisplay: vendorProvidedDate
          ? toCalendarDisplayDate(vendorProvidedDate)
          : selection?.vendorProvidedDateDisplay || '',
        goLiveDateDisplay: goLiveDate
          ? toCalendarDisplayDate(goLiveDate)
          : selection?.goLiveDateDisplay || '',
        cancelDateDisplay: cancelDate
          ? toCalendarDisplayDate(cancelDate)
          : selection?.cancelDateDisplay || '',
        billingStartDateDisplay: billingStartDate
          ? toCalendarDisplayDate(billingStartDate)
          : selection?.billingStartDateDisplay || '',
        billingEndDateDisplay: billingEndDate
          ? toCalendarDisplayDate(billingEndDate)
          : selection?.billingEndDateDisplay || '',
        effectiveDateDisplay: effectiveDate
          ? toCalendarDisplayDate(effectiveDate)
          : selection?.effectiveDateDisplay || '',
        sourceEnrollment: enrollment,
      };
    });
  });
};
